import type { Dispatch, SetStateAction } from 'react';
import { ClipboardDocumentIcon, PauseIcon, PlayIcon } from '@heroicons/react/24/outline';
import { useToast } from '../../../../providers/ToastProvider';
import { channelDef } from './channels';
import { copyToClipboard } from '../../../../utils/clipboard';
import { DirectionToggle } from './DirectionToggle';
import { toDatetimeLocalValue } from './communicationFormatting';
import type { EntryFormValues } from './entryFormValues';

const MAX_CONTACT_NAME_LENGTH = 120;
const MAX_CONTACT_LENGTH = 120;

interface FieldProps {
    label: string;
    children: React.ReactNode;
    /** Sizing within the row - every field stretches to fill the strip's full width (see EntryFieldsSection's own comment), never a left-hugging cluster. */
    className?: string;
}

function Field({ label, children, className = '' }: FieldProps) {
    return (
        <label className={`flex flex-col gap-1.5 ${className}`}>
            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.06em]">
                {label}
            </span>
            {children}
        </label>
    );
}

// Every field gets this so the strip always spans the card's full width,
// however many fields the active channel shows.
const STRETCH_FIELD = 'flex-1 min-w-[150px]';

interface EntryFieldsSectionProps {
    values: EntryFormValues;
    setValues: Dispatch<SetStateAction<EntryFormValues>>;
    disabled: boolean;
    durationError: string | null;
    timerIsRunning: boolean;
    timerElapsedLabel: string;
    onToggleTimer: () => void;
}

// Channel-conditional entry fields, shared by the create page and the
// composer: direction/when/duration on one row, contact/contact-name below.
export function EntryFieldsSection({
    values,
    setValues,
    disabled,
    durationError,
    timerIsRunning,
    timerElapsedLabel,
    onToggleTimer
}: EntryFieldsSectionProps) {
    const { showToast } = useToast();
    const def = channelDef(values.channel);

    const handleCopyContact = async () => {
        const copied = await copyToClipboard(values.contactDisplay);
        showToast(copied ? 'Copied' : 'Could not copy', copied ? 'success' : 'error');
    };

    const handleSetNow = () => {
        setValues((f) => ({ ...f, occurredAtLocal: toDatetimeLocalValue(new Date()) }));
    };

    return (
        <div className="flex flex-col gap-3 bg-gray-50 border border-gray-100 rounded-xl p-3.5">
            <div className="flex items-end gap-2.5 flex-wrap">
                {def.hasDirection && (
                    <Field label="Direction" className={STRETCH_FIELD}>
                        <DirectionToggle
                            value={values.direction}
                            onChange={(direction) => setValues((f) => ({ ...f, direction }))}
                        />
                    </Field>
                )}

                <Field label="When" className={STRETCH_FIELD}>
                    <span className="h-9 rounded-lg border border-gray-300 bg-white flex items-center overflow-hidden">
                        <input
                            type="datetime-local"
                            value={values.occurredAtLocal}
                            disabled={disabled}
                            onChange={(e) =>
                                setValues((f) => ({ ...f, occurredAtLocal: e.target.value }))
                            }
                            className="flex-1 min-w-0 h-full px-2.5 text-[13px] font-medium text-gray-800 border-0 focus:outline-none focus:ring-0 disabled:opacity-50"
                        />
                        <button
                            type="button"
                            disabled={disabled}
                            onClick={handleSetNow}
                            title="Set to now"
                            className="h-full px-2 border-l border-gray-200 text-[11px] font-bold text-gray-500 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                        >
                            NOW
                        </button>
                    </span>
                </Field>

                {def.hasDuration && (
                    <Field label="Duration" className={STRETCH_FIELD}>
                        {timerIsRunning ? (
                            <span className="w-full h-9 pl-2.5 pr-1 rounded-lg bg-[#0F1720] flex items-center gap-2.5">
                                <span className="relative flex items-center justify-center w-2 h-2 shrink-0">
                                    <span className="absolute w-2 h-2 rounded-full bg-[#5CC6BB] opacity-60 animate-ping" />
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#5CC6BB]" />
                                </span>
                                <span className="font-mono text-[14px] font-semibold text-white tabular-nums">
                                    {timerElapsedLabel}
                                </span>
                                <button
                                    type="button"
                                    onClick={onToggleTimer}
                                    title="Stop timer"
                                    className="ml-auto w-7 h-7 rounded-md hover:bg-white/10 flex items-center justify-center text-gray-400 shrink-0"
                                >
                                    <PauseIcon className="h-3.5 w-3.5" />
                                </button>
                            </span>
                        ) : (
                            <div className="relative w-full">
                                <div
                                    className={`h-9 rounded-lg border bg-white flex items-center overflow-hidden ${
                                        durationError ? 'border-red-300' : 'border-gray-300'
                                    }`}
                                >
                                    <input
                                        type="number"
                                        min={1}
                                        step={1}
                                        value={values.durationMinutesDisplay}
                                        disabled={disabled}
                                        onChange={(e) =>
                                            setValues((f) => ({
                                                ...f,
                                                durationMinutesDisplay: e.target.value
                                            }))
                                        }
                                        placeholder="-"
                                        className="h-full flex-1 min-w-[48px] pl-2.5 text-[13px] text-gray-800 border-0 focus:outline-none focus:ring-0 disabled:opacity-50"
                                    />
                                    <span className="text-[11px] font-semibold text-gray-400 pr-2 shrink-0">
                                        min
                                    </span>
                                    <button
                                        type="button"
                                        disabled={disabled}
                                        onClick={onToggleTimer}
                                        className="h-full px-2.5 border-l border-gray-200 flex items-center gap-1.5 text-[12px] font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                                    >
                                        <PlayIcon className="h-3.5 w-3.5 text-gray-400" />
                                        Timer
                                    </button>
                                </div>
                            </div>
                        )}
                        {durationError && (
                            <p className="text-[11px] text-red-500 mt-1">{durationError}</p>
                        )}
                    </Field>
                )}
            </div>

            {(def.contactNameLabel || def.contactLabel) && (
                <div className="flex items-start gap-3 flex-wrap">
                    {def.contactNameLabel && (
                        <Field label={def.contactNameLabel} className={STRETCH_FIELD}>
                            <input
                                type="text"
                                value={values.contactNameDisplay}
                                maxLength={MAX_CONTACT_NAME_LENGTH}
                                disabled={disabled}
                                onChange={(e) =>
                                    setValues((f) => ({ ...f, contactNameDisplay: e.target.value }))
                                }
                                placeholder={def.contactNamePlaceholder ?? undefined}
                                className="w-full h-9 px-3 border border-gray-300 rounded-md text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-gray-800 focus:ring-2 focus:ring-gray-200 disabled:opacity-50"
                            />
                        </Field>
                    )}
                    {def.contactLabel && (
                        <Field label={def.contactLabel} className={STRETCH_FIELD}>
                            <div className="flex items-center gap-1.5">
                                <input
                                    type="text"
                                    value={values.contactDisplay}
                                    maxLength={MAX_CONTACT_LENGTH}
                                    disabled={disabled}
                                    onChange={(e) =>
                                        setValues((f) => ({ ...f, contactDisplay: e.target.value }))
                                    }
                                    placeholder={def.contactPlaceholder ?? undefined}
                                    className="flex-1 min-w-0 h-9 px-3 border border-gray-300 rounded-md text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-gray-800 focus:ring-2 focus:ring-gray-200 disabled:opacity-50"
                                />
                                <button
                                    type="button"
                                    title="Copy"
                                    disabled={!values.contactDisplay.trim()}
                                    onClick={() => void handleCopyContact()}
                                    className="w-9 h-9 rounded-md border border-gray-300 flex items-center justify-center text-gray-500 hover:bg-gray-50 shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    <ClipboardDocumentIcon className="h-4 w-4" />
                                </button>
                            </div>
                        </Field>
                    )}
                </div>
            )}
        </div>
    );
}
